import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { eventFiles } from "@lexical/rich-text";
import { mergeRegister } from "@lexical/utils";
import DragIndicator from "@mui/icons-material/DragIndicator";
import {
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  COMMAND_PRIORITY_HIGH,
  DRAGOVER_COMMAND,
  DROP_COMMAND,
} from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DRAGLEAVE_COMMAND } from "../types/commands";
import {
  hideTargetLine,
  setDragImage,
  setMenuPosition,
  setTargetLine,
} from "../utils/draggable-setters";
import { isHTMLElement, isSVGElement } from "../utils/is-element";
import { useGetBlockElement } from "../utils/use-get-block-element";

import type { LexicalEditor } from "lexical";
import type { DragEvent as ReactDragEvent } from "react";

const DRAGGABLE_BLOCK_MENU_CLASSNAME = "draggable-block-menu";
const DRAG_DATA_FORMAT = "application/x-lexical-drag-block";

function isOnMenu(element: HTMLElement | SVGElement): boolean {
  return Boolean(element.closest(`.${DRAGGABLE_BLOCK_MENU_CLASSNAME}`));
}

function useDraggableBlockMenu(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  isEditable: boolean,
): React.JSX.Element {
  const scrollerElem = anchorElem.parentElement;

  const menuRef = useRef<HTMLDivElement>(null);
  const targetLineRef = useRef<HTMLDivElement>(null);
  const isDraggingBlockRef = useRef<boolean>(false);
  const [draggableBlockElem, setDraggableBlockElem] = useState<HTMLElement | null>(null);
  const getBlockElement = useGetBlockElement();

  useEffect(() => {
    function onMouseMove(event: MouseEvent): void {
      if (!isHTMLElement(event.target) && !isSVGElement(event.target)) {
        setDraggableBlockElem(null);
        return;
      }

      if (isOnMenu(event.target)) {
        return;
      }

      setDraggableBlockElem(getBlockElement(anchorElem, editor, event));
    }

    function onMouseLeave(): void {
      setDraggableBlockElem(null);
    }

    scrollerElem?.addEventListener("mousemove", onMouseMove);
    scrollerElem?.addEventListener("mouseleave", onMouseLeave);

    return () => {
      scrollerElem?.removeEventListener("mousemove", onMouseMove);
      scrollerElem?.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [scrollerElem, anchorElem, editor, getBlockElement]);

  useEffect(() => {
    menuRef.current && setMenuPosition(draggableBlockElem, menuRef.current, anchorElem);
  }, [anchorElem, draggableBlockElem]);

  useEffect(() => {
    function onDragover(event: DragEvent): boolean {
      if (
        !isDraggingBlockRef.current ||
        !isHTMLElement(event.target) ||
        targetLineRef.current === null
      )
        return false;

      const [isFileTransfer] = eventFiles(event);
      if (isFileTransfer) return false;

      const targetBlockElem = getBlockElement(anchorElem, editor, event);
      if (targetBlockElem === null) return false;

      setTargetLine(targetLineRef.current, targetBlockElem, event.clientY, anchorElem);
      // Prevent default event to be able to trigger onDrop events
      event.preventDefault();
      return true;
    }

    function onDrop(event: DragEvent): boolean {
      if (!isDraggingBlockRef.current || !isHTMLElement(event.target)) return false;

      const [isFileTransfer] = eventFiles(event);
      if (isFileTransfer) return false;

      const { dataTransfer, clientY } = event;
      const dragData = dataTransfer?.getData(DRAG_DATA_FORMAT) || "";
      const draggedNode = $getNodeByKey(dragData);
      if (!draggedNode) return false;

      const targetBlockElem = getBlockElement(anchorElem, editor, event);
      if (!targetBlockElem) return false;

      const targetNode = $getNearestNodeFromDOMNode(targetBlockElem);
      if (!targetNode) return false;

      if (targetNode === draggedNode) return true;

      const { top, height } = targetBlockElem.getBoundingClientRect();
      const shouldInsertAfter = clientY - top > height / 2;

      shouldInsertAfter
        ? targetNode.insertAfter(draggedNode)
        : targetNode.insertBefore(draggedNode);

      setDraggableBlockElem(null);

      return true;
    }

    function onDragLeave(): boolean {
      targetLineRef.current && hideTargetLine(targetLineRef.current);
      return false;
    }

    return mergeRegister(
      editor.registerCommand(DRAGOVER_COMMAND, onDragover, COMMAND_PRIORITY_HIGH),
      editor.registerCommand(DROP_COMMAND, onDrop, COMMAND_PRIORITY_HIGH),
      editor.registerCommand(DRAGLEAVE_COMMAND, onDragLeave, COMMAND_PRIORITY_HIGH),
    );
  }, [anchorElem, editor, getBlockElement]);

  const onDragStart = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      const { dataTransfer } = event;
      if (!dataTransfer || !draggableBlockElem) return;

      setDragImage(dataTransfer, draggableBlockElem);
      isDraggingBlockRef.current = true;

      let node = "";
      editor.update(() => {
        node = $getNearestNodeFromDOMNode(draggableBlockElem)?.getKey() ?? "";
      });
      dataTransfer.setData(DRAG_DATA_FORMAT, node);
    },
    [editor, draggableBlockElem],
  );

  const onDragEnd = useCallback(() => {
    isDraggingBlockRef.current = false;
    targetLineRef.current && hideTargetLine(targetLineRef.current);
  }, []);

  return createPortal(
    <>
      <div
        className="icon draggable-block-menu"
        ref={menuRef}
        draggable={true}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        style={{ marginLeft: "-6px" }}
      >
        <div className={isEditable ? "icon" : ""}>
          <DragIndicator sx={{ opacity: 0.3 }} color="primary" />
        </div>
      </div>
      <div className="draggable-block-target-line" ref={targetLineRef} />
    </>,
    anchorElem,
  );
}

export function DraggableBlockPlugin({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement;
}): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  return useDraggableBlockMenu(editor, anchorElem, editor._editable);
}
